import { bucketLog } from '@bucketplace/bucket-log';
import {
  FONT_STYLE,
  MEDIA_QUERY,
  useResponsive,
} from '@bucketplace/design-system';
import { Divider, SelectChip, Sticky } from '@bucketplace/design-system/bds';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import type { ReactElement } from 'react';

import { HOVER } from '@/styles/hover';
import { ellipsis } from '@/styles/text';
import type { PromotionDetailSection } from '../types';

const PC_COLUMN_LENGTH = 4;

interface SectionNavigationProps {
  sectionTitle: string | null;
  sections: PromotionDetailSection[];
  anchorRef?: (node: HTMLDivElement) => void;
  onClick: (title: string, index?: number) => void;
}

export function SectionNavigation({
  sectionTitle,
  sections,
  anchorRef,
  onClick,
}: SectionNavigationProps): ReactElement {
  const { breakpoint } = useResponsive();
  const isMobile = breakpoint === 'mobile' || breakpoint === 'tablet';

  const allSections = [
    { title: '전체', id: '' } as PromotionDetailSection,
  ].concat(sections);
  const remainderSection = allSections.length % PC_COLUMN_LENGTH;
  const concatEmptySections =
    remainderSection >= 1 && remainderSection < 4
      ? allSections.concat(
          [...Array(PC_COLUMN_LENGTH - remainderSection)].map((_) => ({
            title: '',
            id: '',
          })),
        )
      : allSections;
  const selectedSection = !sectionTitle ? '전체' : sectionTitle;

  const handleItemClick = (title: string, index: number): void => {
    bucketLog({
      category: 'click',
      index,
      section: title,
    });

    onClick(title, index - 1);
  };

  return (
    <NavigationSticky
      type="section-navigation"
      direction="top"
      offset={0}
      containerRef={anchorRef}
      zIndex={100}
    >
      <List>
        {concatEmptySections.map(({ title }, index) => {
          const selected = selectedSection === title;
          const itemOptions = {
            key: `${title}_${index + 1}`,
            className: classNames(title, {
              selected,
            }),
            onClick: () => handleItemClick(title ?? '', index),
          };
          if (isMobile && title === '') {
            return null;
          }
          return isMobile ? (
            <MobileItem
              size={32}
              variant="primary1"
              checked={selected}
              {...itemOptions}
            >
              <span>{title}</span>
            </MobileItem>
          ) : (
            <PcItem {...itemOptions}>
              <span>{title}</span>
            </PcItem>
          );
        })}
        {isMobile && <Divider variant="thin" />}
      </List>
    </NavigationSticky>
  );
}

const NavigationSticky = styled(Sticky)`
  display: flex;
  align-items: center;
  z-index: 100;
`;

const List = styled.ul`
  width: 100%;
  display: flex;
  flex-flow: wrap;
  ${({ theme }) => css`
    background-color: ${theme.content.white};
  `}
  ${MEDIA_QUERY.max(
    'tablet',
    `
    padding: 12px 16px 4px 16px;
    > hr {
      width: calc(100% + 32px);
      margin: 4px -16px -4px -16px;
    }
  `,
  )}
`;

const MobileItem = styled(SelectChip)`
  ${FONT_STYLE.subTitle2}
  display: flex;
  max-width: 100%;
  padding: 7px 12px;
  font-weight: 400;
  margin: 0 8px 8px 0;
  ${HOVER`
    cursor: pointer;
  `}
  > span {
    ${ellipsis(1)}
  }
`;

const PcItem = styled.button`
  ${FONT_STYLE.subTitle1}
  width: 25%;
  padding: 13px 8px;
  margin: 0 0 -1px -1px;
  word-break: keep-all;
  font-weight: 700;
  ${({ theme }) => css`
    color: ${theme.content.base1};
    background-color: ${theme.content.base5};
    border: 1px solid ${theme.content.base4};
    &.selected {
      color: ${theme.content.white};
      background-color: ${theme.content.base1};
      border: 1px solid ${theme.content.base1};
    }
  `};
  ${({ theme }) => HOVER`
    &:not(.selected) {
      color: ${theme.content.base2};
    }
  `}
  > span {
    ${ellipsis(2)}
  }
`;
